// Browser-side crash reporter. Captures uncaught errors and unhandled
// promise rejections, then POSTs them to the backend so they land in the
// same structured pino log stream as server crashes — tagged with the
// build release so a stack trace can be tied back to a specific bundle.
//
// Mirrors only — never throws, never blocks. We dedupe identical
// reports inside a short window so a busted render loop can't flood the
// log drain.

const ENDPOINT = `${import.meta.env?.BASE_URL ?? "/"}api/client-errors`;
const RELEASE =
  (import.meta.env?.VITE_APP_RELEASE as string | undefined) ??
  import.meta.env?.MODE;

const recent = new Map<string, number>();
const DEDUPE_WINDOW_MS = 10_000;
const MAX_PER_MINUTE = 20;
let sentInLastMinute = 0;
let windowResetAt = 0;

function isDuplicate(key: string): boolean {
  const now = Date.now();
  for (const [k, ts] of recent) {
    if (now - ts > DEDUPE_WINDOW_MS) recent.delete(k);
  }
  if (recent.has(key)) return true;
  recent.set(key, now);
  return false;
}

function withinRateLimit(): boolean {
  const now = Date.now();
  if (now > windowResetAt) {
    windowResetAt = now + 60_000;
    sentInLastMinute = 0;
  }
  if (sentInLastMinute >= MAX_PER_MINUTE) return false;
  sentInLastMinute += 1;
  return true;
}

function send(payload: Record<string, unknown>): void {
  try {
    const body = JSON.stringify(payload);
    // Prefer sendBeacon so reports survive page unload; fall back to
    // keepalive fetch when sendBeacon isn't available or rejects.
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function"
    ) {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "same-origin",
    }).catch(() => {});
  } catch {
    // Reporter must never throw.
  }
}

function report(
  kind: "error" | "unhandledrejection" | "react",
  err: unknown,
  context?: Record<string, unknown>,
): void {
  try {
    const error =
      err instanceof Error
        ? err
        : new Error(typeof err === "string" ? err : JSON.stringify(err));
    const key = `${kind}|${error.name}|${error.message}|${(error.stack ?? "").slice(0, 200)}`;
    if (isDuplicate(key)) return;
    if (!withinRateLimit()) return;
    send({
      kind,
      name: error.name,
      message: error.message.slice(0, 2000),
      stack: error.stack?.slice(0, 20_000),
      url: typeof location !== "undefined" ? location.href : undefined,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      release: RELEASE,
      context,
    });
  } catch {
    // Swallow — never let the reporter break the app.
  }
}

let installed = false;

export function installClientErrorReporter(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    report("error", event.error ?? event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    report("unhandledrejection", event.reason);
  });
}

export function reportReactError(
  err: unknown,
  context?: Record<string, unknown>,
): void {
  report("react", err, context);
}
